import type {ReactElement} from 'react';
import {useState} from 'react';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import type {OverlayChildren} from 'react-bootstrap/esm/Overlay';
import Tooltip from 'react-bootstrap/esm/Tooltip';
import type {ContextualMenuItem} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {ClickableDiv} from '@Components/clickable-div';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {Text, TextSize, TextType} from '@Components/text';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {AiVideoFilterKey} from '@Panels/ai-video-panel/ai-video-panel.types';
import {assertNever} from '@Utils/assert-never.util';
import {noop} from '@Utils/general.util';
import type {AiVideoSettingsPillProps} from './ai-video-settings-pill.types';
import {
  AI_VIDEO_SETTINGS_PILL_MODAL_PANEL_ID,
  ASPECT_RATIO_PORTRAIT_VALUE,
  AUDIO_OFF_VALUE,
  CAMERA_ANGLE_DEFAULT_VALUE,
  isContextualMenuItemWithIcon,
  isContextualMenuItemWithImage,
  PILL_ICON_ASPECT_RATIO_LANDSCAPE,
  PILL_ICON_ASPECT_RATIO_PORTRAIT,
  PILL_ICON_AUDIO_OFF,
  PILL_ICON_AUDIO_ON,
  PILL_ICON_CAMERA_ANGLE,
  PILL_ICON_QUALITY,
  PILL_ICON_TEXT_SETTING,
  SELECTED_ROW_CHECK_ICON,
  SPOKEN_TEXT_SETTING_VALUES,
} from './ai-video-settings-pill.types';
import styles from './ai-video-settings-pill.module.scss';

export function AiVideoSettingsPill({filterKey, group, isAudioOff, onSelectItem}: AiVideoSettingsPillProps): ReactElement {
  const modalPanelId = `${AI_VIDEO_SETTINGS_PILL_MODAL_PANEL_ID}-${filterKey}`;
  const closeModalForSmallScreen = useDefaultPanelClose(modalPanelId);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const onDropdownOpen = (): void => {
    if (window.innerWidth >= MOBILE_MODAL_WIDTH_THRESHOLD) {
      setIsDropdownOpen(true);
    }
  };

  const onDropdownClose = (): void => {
    setIsDropdownOpen(false);
  };

  const getSelectedTextSettingItem = (): ContextualMenuItem | null => {
    return group.items.find((item) => item.active) ?? null;
  };

  const getSelectedItem = (): ContextualMenuItem => {
    return group.items.find((item) => item.active) ?? group.items[0];
  };

  const onItemClick = (itemIndex: number): void => {
    onSelectItem(itemIndex);
    closeModalForSmallScreen();
  };

  const getPillLabel = (): string => {
    switch (filterKey) {
      case AiVideoFilterKey.QUALITY:
      case AiVideoFilterKey.ASPECT_RATIO:
      case AiVideoFilterKey.AUDIO:
        return getSelectedItem().label;
      case AiVideoFilterKey.TEXT_SETTING:
        return getSelectedTextSettingItem()?.label ?? group.label;
      case AiVideoFilterKey.CAMERA_ANGLE: {
        const selectedItem = getSelectedItem();
        return selectedItem.value === CAMERA_ANGLE_DEFAULT_VALUE ? window.i18next.t('pmwjs_default_angle') : selectedItem.label;
      }
      default:
        return assertNever(filterKey);
    }
  };

  const getPillIcon = (): string => {
    switch (filterKey) {
      case AiVideoFilterKey.QUALITY:
        return getItemIconName(getSelectedItem()) ?? PILL_ICON_QUALITY;
      case AiVideoFilterKey.ASPECT_RATIO:
        return getSelectedItem().value === ASPECT_RATIO_PORTRAIT_VALUE ? PILL_ICON_ASPECT_RATIO_PORTRAIT : PILL_ICON_ASPECT_RATIO_LANDSCAPE;
      case AiVideoFilterKey.AUDIO:
        return getSelectedItem().value === AUDIO_OFF_VALUE ? PILL_ICON_AUDIO_OFF : PILL_ICON_AUDIO_ON;
      case AiVideoFilterKey.CAMERA_ANGLE:
        return PILL_ICON_CAMERA_ANGLE;
      case AiVideoFilterKey.TEXT_SETTING:
        return PILL_ICON_TEXT_SETTING;
      default:
        return assertNever(filterKey);
    }
  };

  const getItemIconName = (item: ContextualMenuItem): string | null => {
    if (isContextualMenuItemWithIcon(item) && item.icon) {
      return item.icon;
    }
    return null;
  };

  const getRowIcon = (item: ContextualMenuItem, iconSize: IconSize = IconSize.SIZE_ICON_16): ReactElement | null => {
    const iconName = getItemIconName(item);
    if (!iconName) {
      return null;
    }
    return <Icon icon={iconName} size={iconSize} className={item.active ? 'ct-brand-primary' : 'ct-neutral-primary'} />;
  };

  const getSelectedCheckIcon = (item: ContextualMenuItem): ReactElement | null => {
    if (!item.active) {
      return null;
    }
    return <Icon icon={SELECTED_ROW_CHECK_ICON} size={IconSize.SIZE_ICON_16} className="ct-brand-primary" />;
  };

  const getRowEnd = (item: ContextualMenuItem): ReactElement | null => {
    if (item.description) {
      return <Text val={item.description} size={TextSize.SM} type={TextType.BODY} className={item.active ? 'ct-brand-primary' : 'ct-neutral-secondary'} />;
    }
    return getSelectedCheckIcon(item);
  };

  const getListRow = (item: ContextualMenuItem, itemIndex: number): ReactElement => {
    return (
      <ClickableDiv
        key={item.value}
        className={`${styles.listRow} ${item.active ? styles.selectedRow : ''}`}
        onClick={(): void => {
          onItemClick(itemIndex);
        }}
      >
        <div className={styles.rowStart}>
          {getRowIcon(item)}
          <Text val={item.label} size={TextSize.SM} type={TextType.BODY} bold={item.active} className={item.active ? 'ct-brand-primary' : 'ct-neutral-primary'} />
        </div>
        {getRowEnd(item)}
      </ClickableDiv>
    );
  };

  const getTextSettingRowDescription = (item: ContextualMenuItem): ReactElement | null => {
    if (!item.description) {
      return null;
    }
    return <Text val={item.description} size={TextSize.SM} type={TextType.BODY} className={item.active ? 'ct-brand-primary' : 'ct-neutral-secondary'} />;
  };

  const getTurnsAudioOnTooltip = (): OverlayChildren => {
    return (
      <Tooltip className={`_rounded-arrow ${styles.rightArrowTooltip}`}>
        <Text val={window.i18next.t('pmwjs_this_will_turn_your_audio_setting_on')} size={TextSize.SM} type={TextType.BODY} />
      </Tooltip>
    );
  };

  const shouldShowTurnsAudioOnTooltip = (item: ContextualMenuItem, isForModal: boolean): boolean => {
    return isAudioOff && !item.active && !isForModal && SPOKEN_TEXT_SETTING_VALUES.includes(item.value);
  };

  const getTextSettingRow = (item: ContextualMenuItem, itemIndex: number, isForModal: boolean): ReactElement => {
    const row = (
      <ClickableDiv
        key={item.value}
        className={`${styles.textSettingRow} ${item.active ? styles.selectedRow : ''}`}
        onClick={(): void => {
          onItemClick(itemIndex);
        }}
      >
        <div className={styles.rowStart}>
          <div className={`${styles.textSettingIconBox} ${item.active ? styles.selectedIconBox : ''}`}>{getRowIcon(item, IconSize.SIZE_ICON_24)}</div>
          <div className={styles.textSettingLabels}>
            <Text val={item.label} size={TextSize.MD} type={TextType.BODY} bold className={item.active ? 'ct-brand-primary' : 'ct-neutral-primary'} />
            {getTextSettingRowDescription(item)}
          </div>
        </div>
        {getSelectedCheckIcon(item)}
      </ClickableDiv>
    );

    if (!shouldShowTurnsAudioOnTooltip(item, isForModal)) {
      return row;
    }
    return (
      <OverlayTrigger key={item.value} overlay={getTurnsAudioOnTooltip()} placement="top-end" trigger={['hover', 'focus']} delay={{show: 200, hide: 200}}>
        <div>{row}</div>
      </OverlayTrigger>
    );
  };

  const getCameraAngleCard = (item: ContextualMenuItem, itemIndex: number): ReactElement => {
    const imageUrl = isContextualMenuItemWithImage(item) ? item.imageUrl : '';
    return (
      <ClickableDiv
        key={item.value}
        className={`flex-v-row gap-2 spacing-p-2 _cursor-pointer hover-transition-all radius-12 ${styles.cameraCard} ${item.active ? styles.selectedCameraCard : ''}`}
        onClick={(): void => {
          onItemClick(itemIndex);
        }}
      >
        <img src={imageUrl} alt={item.label} className={styles.cameraCardImage} />
        <Text val={item.label} size={TextSize.SM} type={TextType.BODY} bold={item.active} className={item.active ? 'ct-brand-primary' : 'ct-neutral-primary'} />
      </ClickableDiv>
    );
  };

  const getPopupHeading = (isForModal: boolean): ReactElement | null => {
    if (filterKey === AiVideoFilterKey.TEXT_SETTING) {
      return <Text val={window.i18next.t('pmwjs_choose_how_text_appears')} size={TextSize.SM} bold type={TextType.BODY} className={`ct-neutral-primary spacing-p-l-1`} />;
    }
    if (filterKey === AiVideoFilterKey.CAMERA_ANGLE && !isForModal) {
      return <Text val={group.label} size={TextSize.SM} type={TextType.BODY} bold className="ct-neutral-primary spacing-p-l-1" />;
    }
    return null;
  };

  const getPopupClassName = (): string => {
    if (filterKey === AiVideoFilterKey.CAMERA_ANGLE) {
      return styles.cameraPopup;
    }
    if (filterKey === AiVideoFilterKey.TEXT_SETTING) {
      return styles.textSettingPopup;
    }
    if (filterKey === AiVideoFilterKey.AUDIO) {
      return styles.audioPopup;
    }
    return styles.listPopup;
  };

  const getPopupContent = (isForModal: boolean): ReactElement => {
    switch (filterKey) {
      case AiVideoFilterKey.QUALITY:
      case AiVideoFilterKey.ASPECT_RATIO:
      case AiVideoFilterKey.AUDIO: {
        return <div className={'flex-v-row gap-2'}>{group.items.map(getListRow)}</div>;
      }
      case AiVideoFilterKey.CAMERA_ANGLE:
        return (
          <div className={'flex-v-row gap-2 spacing-p-t-1'}>
            {getPopupHeading(isForModal)}
            <div className={styles.cameraCardsGrid}>{group.items.map(getCameraAngleCard)}</div>
          </div>
        );
      case AiVideoFilterKey.TEXT_SETTING:
        return (
          <div className={'flex-v-row gap-2 spacing-p-t-1'}>
            {getPopupHeading(isForModal)}
            {group.items.map((item, itemIndex) => getTextSettingRow(item, itemIndex, isForModal))}
          </div>
        );
      default:
        return assertNever(filterKey);
    }
  };

  const getSelector = (): ReactElement => {
    return (
      <PillButton
        size={PillButtonSize.SMALL}
        text={getPillLabel()}
        textSize={TextSize.SM}
        leftIconProps={{icon: getPillIcon(), size: IconSize.SIZE_ICON_16}}
        onClick={noop}
        className={isDropdownOpen ? styles.openPillButton : ''}
      />
    );
  };

  return (
    <Dropdown
      selector={getSelector()}
      popup={getPopupContent(false)}
      onOpen={onDropdownOpen}
      onClose={onDropdownClose}
      popUpHasCustomWidth
      alignment={ALIGNMENT_TYPE.BOTTOM_START}
      position={DROPDOWN_POSITION.FIXED}
      showModalForSmallScreen
      popupClassName={getPopupClassName()}
      topOffset={-6}
      modalTitle={group.label}
      modalClassName={'spacing-p-t-0 spacing-p-b-4 spacing-p-l-4 spacing-p-r-4'}
      modalContent={getPopupContent(true)}
      panelIdForModal={modalPanelId}
    />
  );
}
